<template>
  <div class="flex column v2-daterange-picker" :style="wrapperStyle">
    <div class="text-grey-8" v-if="displayLabel">期間選択</div>
    <date-range-picker
      ref="picker"
      :opens="opens"
      :dateRange="dateRange"
      v-model="dateRange"
      :locale-data="customLocale"
      :ranges="customRanges"
    >
      <template v-slot:input="picker">
        <div class="row items-center justify-between">
          <!-- Date text -->
          <div class="text-grey-8">{{ formatDate(picker) }}</div>

          <!-- Right-aligned icons -->
          <div class="row items-center q-gutter-xs q-ml-auto">
            <q-icon
              name="close"
              class="cursor-pointer text-grey-6"
              size="xs"
              v-if="dateRange.startDate !== ''"
              @click="onClearDateRangeChange()"
            />
            <q-icon :color="color" name="event" class="cursor-pointer calendar-icon" size="sm" />
          </div>
        </div>
      </template>
      <template v-slot:footer="data">
        <div class="row justify-end q-pa-sm" style="border-top: 1px solid #ddd">
          <q-btn no-caps flat class="btn-cancel q-mr-sm" @click="data.clickCancel">{{ $t('cancel') }} </q-btn>
          <q-btn no-caps class="btn-save" @click="onSelect(data)" :disable="data.in_selection">
            {{ $t('save') }}
          </q-btn>
        </div>
      </template>
    </date-range-picker>
  </div>
</template>

<script lang="ts">
import { mixins, Options } from 'vue-class-component'
import { maska } from 'maska'
import AppMixin from '@/components/app/mixins/AppMixin.vue'
import BaseFormMixin from '@/components/common/mixins/BaseFormMixin.vue'
import { constant } from '@/utils/constants'
import { IDateRangePicker } from '@/utils/types'
import { Prop, Watch } from 'vue-property-decorator'
import moment from 'moment'
import { useQuasar } from 'quasar'
import DateRangePicker from 'vue3-daterange-picker'
import { localeData, localeRanges } from '@/utils/datetime'
@Options({
  components: { DateRangePicker },
  directives: { maska },
  emits: ['update:onChangeDateRange', 'update:modelValue'],
})
export default class DateRangeSelectorV2 extends mixins(AppMixin, BaseFormMixin) {
  @Prop()
  modelValue!: IDateRangePicker

  @Prop()
  disable!: boolean

  @Prop({ default: 'left' })
  opens!: string

  @Prop()
  displayLabel!: boolean

  @Prop({ default: 24 })
  iconSize!: number

  @Prop({ default: 12 })
  fontSize!: number

  @Prop()
  isClear!: boolean

  @Prop({ default: '#3982c2' })
  color!: string

  dateFormat = 'YYYY-MM-DD'
  customLocale = localeData(this.dateFormat)
  customRanges = localeRanges()
  dateRange: IDateRangePicker = {
    startDate: '',
    endDate: '',
  }

  date_range = constant.DATE_RANGE
  quasar = useQuasar()

  get value() {
    return this.modelValue
  }

  set value(value) {
    this.$emit('update:modelValue', value)
  }

  get wrapperStyle() {
    return {
      '--picker-border-color': this.color,
    }
  }

  onClearDateRangeChange() {
    this.dateRange = {
      startDate: '',
      endDate: '',
    }

    this.$emit('update:onChangeDateRange', this.dateRange)
  }

  changeDateRange(data: IDateRangePicker) {
    const check = this.checkRange(data.startDate, data.endDate)

    if (check) {
      this.$emit('update:onChangeDateRange', data)
    }
  }

  checkRange(start, end) {
    const from = moment(start, this.dateFormat)
    const to = moment(end, this.dateFormat)
    const diff = to.diff(from, 'days')
    if (diff > this.date_range) {
      this.quasar.notify({
        type: 'negative',
        message: this.$t('errors.over_92_days'),
      })
      return false
    } else {
      return true
    }
  }

  formatDate(date) {
    if (date.startDate && date.endDate) {
      return moment(date.startDate).format(this.dateFormat) + ' - ' + moment(date.endDate).format(this.dateFormat)
    } else {
      return this.dateFormat + ' - ' + this.dateFormat
    }
  }

  onSelect(data) {
    data.clickApply()
    this.changeDateRange(this.dateRange)
  }

  parseDateRange(range) {
    const [startDate, endDate] = range.split(' - ')
    return { startDate, endDate }
  }

  @Watch('value')
  valueChange() {
    if (this.value) {
      this.dateRange.startDate = this.value.startDate
      this.dateRange.endDate = this.value.endDate
    }
  }

  created() {
    // Only set if modelValue (this.value) exists
    if (this.value) {
      this.dateRange.startDate = this.value.startDate
      this.dateRange.endDate = this.value.endDate
    }
  }
}
</script>

<style scoped lang="scss">
.v2-daterange-picker {
  justify-content: center;
  height: 40px;
  border: 1px solid var(--picker-border-color);
  border-radius: 4px !important;
  .vue-daterange-picker {
    text-align: center;
    min-width: 220px !important;
    width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    :deep(.reportrange-text) {
      border: none;
      border-radius: 4px;
    }
    .calendar-icon {
      color: var(--picker-border-color);
    }
  }
}
</style>
